import React from "react";
import {
  consumePendingNewestCaptureCount,
  createDispatchCaptureKeyTracker,
  DISPATCH_CAPTURE_ARRIVAL_HIGHLIGHT_MS,
  takeNewestCaptureKeys,
} from "../lib/dispatchCaptureArrivalHighlight.js";

export default function useDispatchCaptureArrivalHighlight(keys, {
  enabled = false,
  onArrival,
  resetKey = "",
  view,
} = {}) {
  const trackerRef = React.useRef(null);
  if (trackerRef.current == null) {
    trackerRef.current = createDispatchCaptureKeyTracker();
  }
  const resetKeyRef = React.useRef(resetKey);
  const didConsumePendingRef = React.useRef(false);

  const [highlightedKeys, setHighlightedKeys] = React.useState(() => new Set());
  const keysSignature = Array.isArray(keys) ? keys.join("\u0000") : "";
  const onArrivalRef = React.useRef(onArrival);
  onArrivalRef.current = onArrival;

  React.useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const isScopeReset = resetKeyRef.current !== resetKey;
    if (isScopeReset) {
      trackerRef.current = createDispatchCaptureKeyTracker();
      resetKeyRef.current = resetKey;
      setHighlightedKeys(new Set());
    }

    const nextKeys = keysSignature ? keysSignature.split("\u0000") : [];
    const pendingCount = isScopeReset || didConsumePendingRef.current
      ? 0
      : consumePendingNewestCaptureCount(view);
    if (!didConsumePendingRef.current) {
      didConsumePendingRef.current = true;
    }
    const ingested = trackerRef.current.ingest(nextKeys);
    const arrived = ingested.length > 0
      ? ingested
      : takeNewestCaptureKeys(nextKeys, pendingCount);

    if (arrived.length === 0) {
      return undefined;
    }

    setHighlightedKeys((currentKeys) => {
      const nextHighlightedKeys = new Set(currentKeys);
      arrived.forEach((key) => nextHighlightedKeys.add(key));
      return nextHighlightedKeys;
    });
    onArrivalRef.current?.(arrived);
    return undefined;
  }, [enabled, keysSignature, resetKey, view]);

  React.useEffect(() => {
    if (highlightedKeys.size === 0 || typeof window === "undefined") {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setHighlightedKeys(new Set());
    }, DISPATCH_CAPTURE_ARRIVAL_HIGHLIGHT_MS);
    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [highlightedKeys]);

  return highlightedKeys;
}
